import React, { type FC } from 'react'
import { iconButtonClasses, type SxProps, TableCell, tableRowClasses, type Theme } from '@mui/material'

import { isArrayNotEmpty } from '../../../../utils'

import { Translation } from '../../../../domain'

import { FormattedTranslation, useTranslator } from '../../Internationalization'
import { ICON_BUTTON_MEDIUM_SIZE, THEME_SPACING_UNIT } from '../../theme'
import { ActionMenu, type ActionMenuItem } from '../ActionMenu'
import { ACTION_ORDER, defaultIcons, defaultTranslations, DESTRUCTIVE_ACTION } from './Actions'
import { useRequiredSelection, useShowsLoadedRows } from './State'
import type { Line } from './Types'

const TRIGGER = `& > .${iconButtonClasses.root}`

/** In spacing steps */
const TRAILING_COLUMN_PADDING_X = 2

/** `width: 0` shrinks the column to its content, the button and the padding */
export const trailingColumnStyle: SxProps<Theme> = { width: 0, px: TRAILING_COLUMN_PADDING_X }
/** For a fixed layout, which can't shrink a column to its content: the medium icon button and the padding on either side */
export const TRAILING_COLUMN_WIDTH = ICON_BUTTON_MEDIUM_SIZE + 2 * TRAILING_COLUMN_PADDING_X * THEME_SPACING_UNIT

/**
 * The trigger stays hidden, but focusable, until its row is hovered or has focus, or while its menu is open, whose focus is in a portal.
 * Touch screens can't hover, so they always show it.
 * It is pulled 4px into the cell padding above and below, so the 28px button centres on the 20px text line without making the row taller.
 */
const bodyCellStyle: SxProps<Theme> = {
    ...trailingColumnStyle,
    [TRIGGER]: {
        display: 'flex',
        my: -1,
        opacity: 0,
        transition: ({ transitions }) => transitions.create('opacity', { duration: transitions.duration.shortest }),
    },
    [`.${tableRowClasses.root}:hover > &, .${tableRowClasses.root}:focus-within > &`]: { [TRIGGER]: { opacity: 1 } },
    [`${TRIGGER}[aria-expanded="true"]`]: { opacity: 1 },
    '@media (hover: none)': { [TRIGGER]: { opacity: 1 } },
}

/** The actions the toolbar would offer for this row alone, in the same order and with the same icons and labels */
export const RowActionsCell = <L extends Line>({ index, row }: Readonly<{ index: number, row: L }>): ReturnType<FC> => {
    const translator = useTranslator()
    const { onSelect, idField, icons, labels } = useRequiredSelection<L>()
    const actions = onSelect([row])
    const loaded = useShowsLoadedRows()

    const items: ActionMenuItem[] = []
    for (const action of ACTION_ORDER) {
        const onClick = actions[action]
        if (onClick) {
            items.push({
                key: action,
                label: <FormattedTranslation id={labels[action] ?? defaultTranslations[action]} />,
                icon: icons[action] ?? defaultIcons[action],
                destructive: action === DESTRUCTIVE_ACTION,
                /** As the toolbar's: the actions' dialogs only find the record in loaded rows */
                disabled: !loaded,
                onClick: () => onClick([row]),
            })
        }
    }

    return (
        <TableCell sx={bodyCellStyle}>
            {isArrayNotEmpty(items) && (
                <ActionMenu
                    data-testid={`table-row-actions-${index}`}
                    label={translator.formatTranslation(Translation.ROW_ACTIONS, { name: String(row[idField]) })}
                    items={items}
                />
            )}
        </TableCell>
    )
}
